import { useQuery } from '@apollo/client'
import Box from '@components/base/Box'
import LoadingSpinner from '@components/base/LoadingSpinner'
import TableMembers from '@components/metrics/TableMembers'
import TableOrders from '@components/metrics/TableOrders'
import TableOrganisers from '@components/metrics/TableOrganisers'
import { GET_ACTIVE_MEMBERSHIPS_BY_COMPANY } from '@graphql/events/GetMembershipsByCompany'
import { GET_ORDERS_BY_ORGANISATION } from '@graphql/events/GetOrdersByCompany'
import { GET_FULL_ORGANISERS_BY_COMPANY } from '@graphql/users/getFullOrganiserByCompany'
import { GET_ORGANISATION_ORGANISERS } from '@graphql/users/manageOrganisationOrganisers'
import { useAuth } from '@libs/Auth/useAuth'
import React from 'react'
import { cleanGqlPayload } from '@libs/Utility/util'

export const OrderManager = () => {
  const { profile, isOrganizer } = useAuth()

  const { data, loading, error, refetch } = useQuery(
    GET_ORDERS_BY_ORGANISATION,
    {
      variables: { organisationName: profile?.company ?? '' },
      skip: !profile?.company || !isOrganizer,
    }
  )

  if (!data?.getOrdersByOrganisation && loading)
    return <LoadingSpinner className="mt-1 mx-auto" size="medium" />

  return (
    <Box className="flex flex-col items-center mt-14 text-white w-full">
      <TableOrders
        orders={data?.getOrdersByOrganisation ?? []}
        refetch={refetch}
      />
    </Box>
  )
}

export const OrganiserManager = ({ className }: { className: string }) => {
  const { profile, hasPermission, isAdmin } = useAuth()
  const organisationName = profile?.company ?? profile?.university ?? ''
  const canManageOrganisers =
    isAdmin || hasPermission(`USER::MANAGE_ORGANISATION::${organisationName}`)

  const shouldUseOrganisationOrganisersQuery =
    !!organisationName && canManageOrganisers
  const shouldUseCompanyOrganisersQuery =
    !!organisationName && !shouldUseOrganisationOrganisersQuery

  const { data, loading, refetch } = useQuery(GET_FULL_ORGANISERS_BY_COMPANY, {
    variables: { company: organisationName },
    skip: !shouldUseCompanyOrganisersQuery,
  })

  const {
    data: organisationOrganiserData,
    loading: organisationOrganiserLoading,
    refetch: refetchOrganisationOrganisers,
  } = useQuery(GET_ORGANISATION_ORGANISERS, {
    variables: { organisationName },
    skip: !shouldUseOrganisationOrganisersQuery,
    errorPolicy: 'all',
  })

  const organisers =
    organisationOrganiserData?.getOrganisationOrganisers?.organisers ??
    data?.getOrganiserByCompany.items

  const refetchOrganisers = async () => {
    if (shouldUseOrganisationOrganisersQuery) {
      await refetchOrganisationOrganisers()
      return
    }

    await refetch()
  }

  if (
    shouldUseCompanyOrganisersQuery &&
    !data?.getOrganiserByCompany &&
    loading
  )
    return <LoadingSpinner className="mt-1 mx-auto" size="medium" />

  if (
    shouldUseOrganisationOrganisersQuery &&
    !organisers &&
    organisationOrganiserLoading
  )
    return <LoadingSpinner className="mt-1 mx-auto" size="medium" />

  return (
    <Box className="flex flex-col items-center mt-14 text-white w-full">
      <TableOrganisers
        users={cleanGqlPayload(organisers) ?? []}
        pendingInvites={
          cleanGqlPayload(
            organisationOrganiserData?.getOrganisationOrganisers?.pendingInvites
          ) ?? []
        }
        className={className}
        organisationName={organisationName}
        refetch={refetchOrganisers}
      />
    </Box>
  )
}

export const MemberManager = () => {
  const { profile, isOrganizer } = useAuth()

  const { data, loading, error, refetch } = useQuery(
    GET_ACTIVE_MEMBERSHIPS_BY_COMPANY,
    {
      variables: { organisationName: profile?.company ?? '' },
      skip: !profile?.company || !isOrganizer,
    }
  )

  if (!data?.getActiveMembershipsByCompany && loading)
    return <LoadingSpinner className="mt-1 mx-auto" size="medium" />

  return (
    <Box className="flex flex-col items-center mt-14 text-white w-full">
      <TableMembers
        orders={data?.getActiveMembershipsByCompany ?? []}
        refetch={refetch}
      />
    </Box>
  )
}
